import type {
	GlobalGateway,
	IndiaGateway,
	LegacyGateway,
	PricingRecord,
} from "@/lib/pricing-record";

const DEFAULT_INDIA_GATEWAY: Exclude<IndiaGateway, null> = "payu";
const DEFAULT_GLOBAL_GATEWAY: Exclude<GlobalGateway, null> = "stripe";

export interface CheckoutPlan {
	id: number;
	name: string;
	tier?: string | null;
	inr: number;
	usd: number;
	type: string;
	india_gateway: IndiaGateway;
	india_payu_product_info: string | null;
	india_stripe_price_id: string | null;
	global_gateway: GlobalGateway;
	global_stripe_price_id: string | null;
	payment_gateway?: LegacyGateway;
}

export function findInitialCheckoutPlan(
	records: PricingRecord[],
	planId: number | null,
): PricingRecord | null {
	if (!Number.isInteger(planId) || !planId || planId <= 0) return null;
	return records.find((record) => record.id === planId) ?? null;
}

function normalizeGatewayText(value: string | null | undefined): string | null {
	const normalized = value?.trim().toLowerCase();
	return normalized || null;
}

function normalizeIndiaGateway(value: string | null | undefined): IndiaGateway {
	return normalizeGatewayText(value) === "payu" ? "payu" : null;
}

function normalizeGlobalGateway(
	value: string | null | undefined,
): GlobalGateway {
	return normalizeGatewayText(value) === "stripe" ? "stripe" : null;
}

function normalizeLegacyGateway(
	value: string | null | undefined,
): LegacyGateway {
	const normalized = normalizeGatewayText(value);
	if (normalized === "payu" || normalized === "stripe") return normalized;
	return null;
}

export function toCheckoutPlan(record: PricingRecord): CheckoutPlan {
	const legacyGateway = normalizeLegacyGateway(record.payment_gateway);

	return {
		id: record.id,
		name: record.name,
		tier: record.tier,
		inr: record.price_in_inr,
		usd: record.price_in_usd,
		type: record.type,
		india_gateway:
			normalizeIndiaGateway(record.india_gateway) ?? DEFAULT_INDIA_GATEWAY,
		india_payu_product_info: record.india_payu_product_info,
		india_stripe_price_id: record.india_stripe_price_id,
		global_gateway:
			normalizeGlobalGateway(record.global_gateway) ??
			normalizeGlobalGateway(record.payment_gateway) ??
			DEFAULT_GLOBAL_GATEWAY,
		global_stripe_price_id: record.global_stripe_price_id,
		payment_gateway: legacyGateway,
	};
}
